import { useEffect, useState } from 'react';
import { Box, Link, Typography } from '@mui/material';
import { docs } from './docsStyles';

// Section anchors for a component page. Shared by the detail view (which renders the
// matching `id`s) and this right-rail table of contents. Ordered top-to-bottom.
export const COMPONENT_SECTIONS = [
  { id: 'description', labelKey: 'internalLibraries.description_title', label: 'Description' },
  { id: 'playground', labelKey: 'internalLibraries.playground_title', label: 'Playground' },
  { id: 'usage', labelKey: 'internalLibraries.usage_title', label: 'Usage' },
  { id: 'props', labelKey: 'internalLibraries.props_title', label: 'Props' },
  { id: 'dependencies', labelKey: 'internalLibraries.dependencies_title', label: 'Dependencies' },
];

// Offset so scrolled-to sections clear the sticky app header.
export const SCROLL_MARGIN_TOP = 88;

// Floating "On this page" index (MUI-docs style) with a lightweight scroll-spy that
// highlights the section currently in view. Remount it (via a `key` on the parent) when
// the selected component changes so it observes the fresh section nodes.
function OnThisPage({ sections = COMPONENT_SECTIONS, t }) {
  const [activeId, setActiveId] = useState(sections[0]?.id);

  useEffect(() => {
    const elements = sections
      .map(section => document.getElementById(section.id))
      .filter(Boolean);
    if (elements.length === 0) return undefined;

    const observer = new IntersectionObserver(
      entries => {
        const visible = entries
          .filter(entry => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: '0px 0px -65% 0px', threshold: [0, 1] }
    );

    elements.forEach(element => observer.observe(element));
    return () => observer.disconnect();
  }, [sections]);

  const handleClick = (event, id) => {
    event.preventDefault();
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveId(id);
  };

  return (
    <Box sx={{ position: 'sticky', top: SCROLL_MARGIN_TOP, fontFamily: docs.font }}>
      <Typography
        sx={{
          fontFamily: docs.font,
          fontSize: 11,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 0.8,
          color: docs.textFaint,
          px: 1.5,
          mb: 1,
        }}>
        {t('internalLibraries.on_this_page', 'On this page')}
      </Typography>
      <Box component="nav" sx={{ borderLeft: '1px solid', borderColor: docs.border }}>
        {sections.map(section => {
          const isActive = section.id === activeId;
          return (
            <Link
              key={section.id}
              href={`#${section.id}`}
              underline="none"
              onClick={event => handleClick(event, section.id)}
              sx={{
                display: 'block',
                pl: 1.5,
                py: 0.6,
                ml: '-1px',
                borderLeft: '2px solid',
                borderColor: isActive ? docs.textStrong : 'transparent',
                color: isActive ? docs.textStrong : docs.textMuted,
                fontFamily: docs.font,
                fontSize: 13,
                fontWeight: isActive ? 600 : 400,
                '&:hover': { color: docs.textStrong },
              }}>
              {t(section.labelKey, section.label)}
            </Link>
          );
        })}
      </Box>
    </Box>
  );
}

export default OnThisPage;
